/**
 * Where a registration came from (Engagement Center, Paket 1).
 *
 * The website appends the ad parameters to every link to /register; nothing is stored in the
 * browser. The sign-up page reads them from `location.search` and sends them as `attribution`.
 * Same whitelist and limits as the API (engagement/growth/attribution.ts): unknown keys are dropped,
 * over-long values are dropped (not cut — a truncated gclid is worse than none).
 */

export const ATTRIBUTION_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'gbraid',
  'wbraid',
  'fbclid',
  'fbp',
  'fbc',
  'lp',
  'exp',
  'ref',
  'vid',
] as const;

export type AttributionKey = (typeof ATTRIBUTION_KEYS)[number];
export type Attribution = Partial<Record<AttributionKey, string>>;

export const ATTRIBUTION_MAX_LENGTH = 200;

const hostOf = (url: string): string | undefined => {
  try {
    return new URL(url).host || undefined;
  } catch {
    return undefined;
  }
};

/**
 * @param search   `location.search` of the sign-up page
 * @param referrer `document.referrer` — used as `ref` (host only) when the link carried no `ref`
 *                 and the referrer is not the app itself
 * @param ownHost  the app's host, so an in-app navigation does not count as a referrer
 */
export const parseAttribution = (search: string, referrer = '', ownHost = ''): Attribution | undefined => {
  let params = new URLSearchParams(search);
  // A visitor who is not logged in lands on /welcome?redirectTo=/register?utm_…, so the ad
  // parameters arrive one level down. Without this every paid sign-up looked unattributed.
  const nested = params.get('redirectTo');
  if (nested && !ATTRIBUTION_KEYS.some((k) => params.has(k))) {
    const q = nested.indexOf('?');
    if (q >= 0) params = new URLSearchParams(nested.slice(q + 1));
  }
  const out: Attribution = {};
  for (const key of ATTRIBUTION_KEYS) {
    const raw = params.get(key);
    if (raw === null) continue;
    const value = raw.trim();
    if (!value || value.length > ATTRIBUTION_MAX_LENGTH) continue;
    out[key] = value;
  }
  if (!out.ref && referrer) {
    const host = hostOf(referrer);
    if (host && host !== ownHost) out.ref = host;
  }
  return Object.keys(out).length > 0 ? out : undefined;
};

/**
 * Meta's pixel cookies (_fbp, _fbc). They exist only if the visitor allowed the pixel on
 * the website (it sets them on .secret-ai-agent.de, so the app can read them). Sent along
 * so the Conversions API can match the registration and the purchase to the ad.
 */
export const readMetaCookies = (cookie: string): Attribution => {
  const out: Attribution = {};
  for (const part of cookie.split(';')) {
    const [rawName, ...rest] = part.trim().split('=');
    const value = decodeURIComponent(rest.join('='));
    if (!value || value.length > ATTRIBUTION_MAX_LENGTH) continue;
    if (rawName === '_fbp') out.fbp = value;
    if (rawName === '_fbc') out.fbc = value;
  }
  return out;
};

/** Browser convenience: reads window.location / document.referrer / the Meta pixel cookies. */
export const readAttributionFromBrowser = (search: string): Attribution | undefined => {
  const referrer = typeof document !== 'undefined' ? document.referrer : '';
  const ownHost = typeof window !== 'undefined' ? window.location.host : '';
  const fromUrl = parseAttribution(search, referrer, ownHost);
  let meta: Attribution = {};
  try {
    meta = typeof document !== 'undefined' ? readMetaCookies(document.cookie) : {};
  } catch {
    meta = {};
  }
  const merged = { ...meta, ...(fromUrl ?? {}) };
  return Object.keys(merged).length > 0 ? merged : undefined;
};

export interface SignUpForm {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  companyName: string;
  language: string;
  consentMarketingEmail: boolean;
  consentAdMeasurement: boolean;
  attribution?: Attribution;
}

/** Body of POST /auth/register as the sign-up page sends it. Consents are sent as given (default false). */
export const buildRegisterPayload = (f: SignUpForm) => ({
  // The button is gated on the legal terms, but the server enforces it independently.
  acceptedLegalTerms: true as const,
  firstName: f.firstName.trim(),
  lastName: f.lastName.trim(),
  email: f.email.trim(),
  password: f.password,
  companyName: f.companyName.trim() !== '' ? f.companyName.trim() : undefined,
  language: f.language,
  attribution: f.attribution,
  consentMarketingEmail: f.consentMarketingEmail === true,
  consentAdMeasurement: f.consentAdMeasurement === true,
});
